import { Badge } from '@/components/ui/badge';
import type { WorkflowStage } from '@/components/forms/WorkflowStageSelect';

interface ProjectStageBadgeProps {
  workflowStage: WorkflowStage;
  updatedAt: string; // ISO date string from backend
  className?: string;
}

// Map workflow stage values to human-readable labels
// Phase 7.5.13: Updated with new proposal stages and on_hold
const workflowStageLabels: Record<WorkflowStage, string> = {
  proposal_draft: 'Proposal: Draft',
  proposal_sent: 'Proposal: Sent',
  proposal_declined: 'Proposal: Declined',
  project_setup: 'Project Setup',
  design_development: 'Design Development',
  permit_preparation: 'Permit Preparation',
  in_permit_review: 'In Permit Review',
  construction_administration: 'Construction Administration',
  on_hold: 'On Hold',
  complete: 'Complete',
};

/**
 * Single context-aware badge for project stage/status (Issue #11).
 *
 * Consolidates WorkflowStageBadge + ProposalStatusBadge logic to eliminate duplicate "Active" labels.
 *
 * Display logic:
 * - For proposals: "Proposal: Active" (if < 90 days) or "Proposal: Archived" (if >= 90 days)
 * - For accepted projects: Stage name ("Design Development", "Permit Preparation", etc.)
 *
 * Styling:
 * - Active proposals: default variant (blue)
 * - Archived proposals: secondary variant (gray)
 * - Other stages: outline variant (subtle)
 */
export function ProjectStageBadge({
  workflowStage,
  updatedAt,
  className,
}: ProjectStageBadgeProps) {
  // For proposals: Show "Proposal: Active/Archived" based on 90-day rule
  // Phase 7.5.13: Check for all proposal stages
  if (['proposal_draft', 'proposal_sent', 'proposal_declined'].includes(workflowStage)) {
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const daysSinceUpdate = Math.floor((now.getTime() - updatedDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceUpdate < 90) {
      return (
        <Badge variant="default" className={className}>
          Proposal: Active
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className={className}>
        Proposal: Archived
      </Badge>
    );
  }

  // For accepted projects: Show stage name
  return (
    <Badge variant="outline" className={className}>
      {workflowStageLabels[workflowStage]}
    </Badge>
  );
}
